<template>
    <div class="card-text journey-planner">
        <div :class="journeyPlannerInputClasses">
            <div class="d-flex flex-column flex-md-row">
                <div class="d-lg-flex d-none d-md-flex justify-content-center align-items-baseline not-widget">
                    <template v-if="alwaysShowForm">
                        <span class="d-none d-md-block d-lg-none">Find&nbsp;</span>
                        <span class="d-none d-lg-block">Show me&nbsp;</span>
                    </template>
                    <span v-else class="d-none d-lg-block">We're showing you&nbsp;</span>
                    <div class="h-24"></div>
                    <span class="d-md-none mr-2 not-widget"> journeys</span>
                    <span class="d-none d-md-block mr-2">
                        <!-- eslint-disable-next-line max-len -->
                        <span class="d-lg-inline d-none">j</span><span class="d-inline d-lg-none">{{ alwaysShowForm ? 'j' : 'J' }}</span>ourneys from
                    </span>
                </div>
                <div class="mr-md-2 flex-grow-1 position-relative">
                    <label class="d-md-none small mb-0">
                        From:
                    </label>
                    <StationPicker
                        ref="fromStation"
                        :placeholder="'Enter origin station...'"
                        :start-value="model.station.from.name ? model.station.from.name : null"
                        :extra-classes="'from-station-input'"
                        @pick="item => model.station.from=item"
                        @emptied="() => model.station.from={key: '', name: ''}"
                    ></StationPicker>
                </div>
                <div class="mr-md-2 flex-grow-1 d-flex align-items-baseline position-relative">
                    <span class="d-none d-md-block mr-2">
                        to
                    </span>
                    <div id="to-station" class="w-100">
                        <label class="d-md-none small mb-0">
                            To:
                        </label>
                        <StationPicker
                            :placeholder="'Enter destination station...'"
                            :start-value="model.station.to.name ? model.station.to.name : null"
                            :extra-classes="'to-station-input'"
                            @pick="item => model.station.to=item"
                            @emptied="() => model.station.to={key: '', name: ''}"
                        ></StationPicker>
                        <div class="invalid-feedback"></div>
                    </div>
                </div>
                <div class="d-block d-md-flex switch-stations-container">
                    <div
                        class="btn-outer align-items-center switch-stations" aria-roledescription="button" tabindex="0"
                        @click="switchStations" @keyup.enter="switchStations"
                    >
                        <div class="d-none d-md-inline-block not-widget">
                            <font-awesome-icon
                                icon="fa-solid fa-arrow-right-arrow-left" class="ml-1 mr-1 switch-icon"
                            ></font-awesome-icon>
                        </div>
                        <div class="small d-md-none widget">
                            <font-awesome-icon
                                icon="fa-solid fa-arrow-down-arrow-up" class="mt-0 pt-0 ml-1 mr-1"
                            ></font-awesome-icon>
                            Swap
                        </div>
                    </div>
                </div>
            </div>
            <div class="d-flex flex-column flex-md-row">
                <div class="d-flex align-items-baseline">
                    <span class="mr-2 d-none d-lg-block">
                        Leaving on
                    </span>
                    <span class="mr-md-1 mr-lg-2 d-none d-md-block d-lg-none">
                        Leaving
                    </span>
                    <div class="mr-md-1 mr-lg-2 w-100 w-md-auto">
                        <label class="d-md-none small mb-0">
                            Leaving on:
                        </label>
                        <date-picker
                            id="date-picker-outbound" v-model="model.outbound" class="w-100"
                            :disruptions="disruptions"
                            @input="dateChanged(true)"
                        >
                            Add an outbound date
                        </date-picker>
                        <div id="outbound-date-warning" class="invalid-feedback"></div>
                    </div>
                </div>
                <div class="d-flex align-items-baseline">
                    <template v-if="isValidDate(model.return)">
                        <span class="mr-2 d-none d-lg-block">
                            and returning on
                        </span>
                        <span class="mr-md-1 mr-lg-2 d-none d-md-block d-lg-none">
                            returning
                        </span>
                    </template>
                    <div id="return-date" class="w-100 w-md-auto">
                        <span v-if="model.return.date" class="d-md-none small mb-0">
                            Returning on:
                        </span>
                        <date-picker
                            id="date-picker-return"
                            ref="returnDatePicker" v-model="model.return" :min="model.outbound.date"
                            :removable="true"
                            :disruptions="disruptions"
                            @input="dateChanged(false)"
                        >
                            Add a return...
                        </date-picker>
                        <div class="invalid-feedback"></div>
                    </div>
                </div>
            </div>
            <div class="d-flex flex-column flex-md-row">
                <div class="d-flex align-items-baseline">
                    <label class="mr-2">
                        <input ref="oneWayJourneyCheckbox" type="checkbox" @input="clickOneWayJourney"> One-way journey
                    </label>
                </div>
            </div>
            <div id="buttons" class="d-flex align-items-center flex-wrap">
                <button v-if="isDevApi()" class="btn btn-outline-info mr-2" @click="lichfield()">
                    Lichfield
                </button>
                <passenger-picker
                    ref="paxPicker"
                    :value="model.passengers"
                    :railcards="railcards"
                    @input="updatePassengers"
                ></passenger-picker>
                <input-button
                    v-for="option in selectedOptions" :key="option" :icon="optionIcon(option)"
                    data-toggle="modal" data-target="#moreOptionsModal" data-backdrop="static"
                    data-keyboard="false"
                    @click-inner="remove(option)"
                >
                    {{ optionDisplay(option) }}
                </input-button>
                <input-button
                    icon="add" type="dashed"
                    data-toggle="modal" data-target="#moreOptionsModal" data-backdrop="static"
                    data-keyboard="false"
                    :use-click-event-for-all="true"
                >
                    More Options
                </input-button>
                <button
                    v-tooltip="isRequestValid ? null : invalidReason"
                    :disabled="!isRequestValid"
                    class="btn search"
                    :class="buttonClasses"
                    @click="didClickSearch"
                >
                    <SearchButtonLoadingSpinner></SearchButtonLoadingSpinner>
                    {{ buttonLabel }}
                </button>
            </div>
        </div>

        <div
            id="moreOptionsModal" class="modal fade" tabindex="-1" role="dialog"
            aria-labelledby="moreOptionsModalLabel"
            aria-hidden="true"
        >
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 id="moreOptionsModalLabel" class="modal-title">
                            More Options
                        </h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">
                                &times;
                            </span>
                        </button>
                    </div>
                    <div ref="scrollableContent" class="modal-body more-options">
                        <div class="card-text options-container">
                            <options-group
                                ref="moreOptionsGroup" v-model="modifiedOptions"
                                :config="moreOptionsModalConfig"
                                @input="checkForConflictingOptions"
                            ></options-group>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button
                            id="cancel-button" type="button" class="btn" data-dismiss="modal"
                            @click="resetModifiedOptions"
                        >
                            Cancel
                        </button>
                        <button
                            id="apply-button" type="button"
                            class="btn btn-primary"
                            @click="apply"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
            .
        </div>
        <div id="tcbc-migration" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered" role="document">
                <div class="modal-content">
                    <div class="modal-body d-flex flex-column align-items-center p-2 md:p-0">
                        <img src="/images/tcbc.png" alt="TCBC" class="mb-5 mt-3">
                        <h4 class="text-blue-dark mb-4 text-center">
                            TrainsCanBeCheaper has merged with TrainSplit
                        </h4>
                        <p class="text-center lead">
                            trainscanbecheaper.info used to offer an online booking engine without splits.
                        </p>
                        <p class="text-center mb-3">
                            It's now entirely possible on the TrainSplit website to turn off splits; which we've done for you.
                        </p>
                        <button id="tcbc-ok" class="btn btn-lg btn-outline-primary">
                            Got it!
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as he from 'he';
import { DateTime } from 'luxon';
import Vue from 'vue';
import InputButton from './InputButton';
import DatePicker from './DatePicker';
import StationPicker from './StationPicker';
import OptionsGroup from './OptionsGroup';
import PassengerPicker from './PassengerPicker';
import SearchButtonLoadingSpinner from './icons/SearchButtonLoadingSpinner';

function getDefaultNowBasedTime(extraMins = 30) {
    let nowTime = new Date((new Date()).getTime() + (extraMins * 60000));

    const nextFifteen = Math.round(nowTime.getMinutes() / 15) * 15;
    nowTime = new Date(nowTime.getTime() + ((nextFifteen - nowTime.getMinutes()) * 60000));
    const newTime = DateTime.fromJSDate(nowTime, {})
        .setZone('Europe/London')
        .set({ second: 0 });
    const dateFormatted = newTime.toISODate();
    const timeFormatted = newTime.setLocale('en')
        .toLocaleString(DateTime.TIME_24_SIMPLE);
    return {
        dateFormatted,
        timeFormatted,
    };
}

function getDefaultNowBasedReturnTime(currentTime = DateTime.utc()) {
    // Current time with minutes/seconds truncated.
    let returnTime = currentTime.setZone('Europe/London')
        .set({
            minute: 0,
            second: 0,
        });

    // 00:00 -> 16:00: Add 7 hours
    // 17:00 -> 21:00: Set to 23:00
    // 22:00 -> 23:00: Set to 12:00 next day
    if (returnTime.hour <= 16) {
        returnTime = returnTime.plus({ hours: 7 });
    } else if (returnTime.hour <= 21) {
        returnTime = returnTime.set({ hour: 23 });
    } else {
        returnTime = returnTime.plus({ days: 1 })
            .set({ hour: 12 });
    }

    const dateFormatted = returnTime.toISODate();
    const timeFormatted = returnTime.setLocale('en')
        .toLocaleString(DateTime.TIME_24_SIMPLE);
    return {
        dateFormatted,
        timeFormatted,
    };
}

export default {
    name: 'JourneyPlanner',
    components: {
        SearchButtonLoadingSpinner,
        PassengerPicker,
        InputButton,
        DatePicker,
        OptionsGroup,
        StationPicker,
    },
    props: {
        stations: {
            type: Array,
            default() {
                return [];
            },
        },
        railcards: {
            type: Array,
            default() {
                return [];
            },
        },
        operators: {
            type: Array,
            default() {
                return [];
            },
        },
        alwaysShowForm: {
            type: Boolean,
            required: false,
            default: false,
        },
        overrideFromUic: {
            type: String,
            required: false,
            default: undefined,
        },
        overrideToUic: {
            type: String,
            required: false,
            default: undefined,
        },
        parameters: {
            type: Object,
            required: false,
            default() {
                const outboundTime = getDefaultNowBasedTime();
                const returnTime = getDefaultNowBasedReturnTime();
                return {
                    fromStation: this.$props.overrideFromUic ? this.$props.overrideFromUic : '',
                    toStation: this.$props.overrideToUic ? this.$props.overrideToUic : '',
                    searchBy: 0,
                    outboundDate: `${outboundTime.dateFormatted}T${outboundTime.timeFormatted}`,
                    outboundDateDepartAfter: true,
                    returnDate: `${returnTime.dateFormatted}T${returnTime.timeFormatted}`,
                    returnDateDepartAfter: true,
                    adults: 1,
                    children: 0,
                    railcard: 'none',
                    numberOfRailcards: 0,
                    firstClass: false,
                    flexible: false,
                    useSplitTickets: true,
                    noSplitFaresLU: false,
                    advancedOutbound: null,
                    advancedReturn: null,
                };
            },
        },
        searchResultsTitle: {
            type: Boolean,
            required: false,
            default: false,
        },
        isDirectus: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            invalidReason: null,
            model: {
                station: {
                    from: {
                        name: '',
                        key: '',
                    },
                    to: {
                        name: '',
                        key: '',
                    },
                },
                searchBy: 0,
                outbound: {
                    date: null,
                    time: null,
                    dtObj: null,
                    after: true,
                },
                return: {
                    date: null,
                    time: null,
                    dtObj: null,
                    after: true,
                },
                firstClass: false,
                flexible: false,
                passengers: {
                    adults: 1,
                    children: 0,
                    railcard: 'none',
                    railcardsRequired: 0,
                },
                options: {
                    useSplitTickets: true,
                    noSplitFaresLU: false,
                    advanced: {
                        intensive: false,
                        travelcardType: ['0'],
                        maxZoneCovered: '0',
                        outboundOptions: {
                            routes: {
                                via: [],
                                avoid: [],
                            },
                            travelModes: Object.keys(this.travelModes()),
                            changes: {
                                changes: null,
                                time: null,
                            },
                            operators: {
                                include: [],
                                exclude: [],
                            },
                        },
                        useSameOptions: true,
                        returnOptions: {
                            routes: {
                                via: [],
                                avoid: [],
                            },
                            travelModes: Object.keys(this.travelModes()),
                            changes: {
                                changes: null,
                                time: null,
                            },
                            operators: {
                                include: [],
                                exclude: [],
                            },
                        },
                    },
                },
            },
            modifiedOptions: null,
            stationCache: {},
            lastSearchGuid: null,
        };
    },
    computed: {
        isMobile() {
            return window.matchMedia('(max-width: 992px)').matches;
        },
        disruptions() {
            const val = document.querySelector('#widespreadDisruptions').innerHTML;
            return JSON.parse(he.decode(val));
        },
        moreOptionsModalConfig() {
            const crossLondonFlag = !!document.querySelector('meta[name=brand-cross-london-fares-enabled]')
                .getAttribute('content');
            return !this.modifiedOptions ? {
                options: {},
            } : {
                options: {
                    useSplitTickets: {
                        label: 'Use Split Tickets',
                        type: 'OptionSwitch',
                        nullValue: false,
                    },
                    noSplitFaresLU: {
                        label: 'No Cross-London Fares',
                        longLabel: 'Let me use Oyster/contactless to travel cross-London',
                        type: 'OptionSwitch',
                        hidden: !crossLondonFlag,
                        // eslint-disable-next-line max-len
                        supplementary: 'If enabled, fares for crossing London will be excluded. <a href="/no-split-fares-lu" target="_blank">Why is this useful?</a>',
                        nullValue: false,
                    },
                    advanced: {
                        label: 'Advanced',
                        type: 'OptionsGroup',
                        borderless: true,
                        options: {
                            intensive: {
                                heading: 'Search Options',
                                label: 'Intensive search (more obscure routes with potentially cheaper tickets)',
                                type: 'OptionSwitch',
                                nullValue: false,
                            },
                            outboundOptions: {
                                type: 'OptionsGroup',
                                borderless: true,
                                options: {
                                    routes: {
                                        heading: `Route Options${this.advancedOutboundSuffix}`,
                                        type: 'RouteOptions',
                                        options: this.routeOptions(),
                                        nullValue: {
                                            via: [],
                                            avoid: [],
                                        },
                                    },
                                    travelModes: {
                                        heading: `Travel Modes${this.advancedOutboundSuffix}`,
                                        type: 'RadioSelectors',
                                        options: this.travelModes(),
                                        style: 'checkbox',
                                        styleClass: 'column',
                                        nullValue: Object.keys(this.travelModes()),
                                    },
                                    changes: {
                                        heading: `Changes${this.advancedOutboundSuffix}`,
                                        type: 'ChangesInput',
                                        nullValue: {
                                            changes: null,
                                            time: null,
                                        },
                                    },
                                    operators: {
                                        heading: `Operators${this.advancedOutboundSuffix}`,
                                        type: 'OperatorSelector',
                                        options: this.operators,
                                        nullValue: {
                                            include: [],
                                            exclude: [],
                                        },
                                    },
                                },
                            },
                            useSameOptions: {
                                label: 'Use these options for return',
                                type: 'OptionSwitch',
                                nullValue: true,
                            },
                            returnOptions: {
                                type: 'OptionsGroup',
                                borderless: true,
                                options: {
                                    routes: {
                                        heading: `Route Options${this.advancedReturnSuffix}`,
                                        type: 'RouteOptions',
                                        options: this.routeOptions(),
                                        hidden: this.isHiddenReturnAdvanced,
                                        nullValue: {
                                            via: [],
                                            avoid: [],
                                        },
                                    },
                                    travelModes: {
                                        heading: `Travel Modes${this.advancedReturnSuffix}`,
                                        type: 'RadioSelectors',
                                        options: this.travelModes(),
                                        style: 'checkbox',
                                        styleClass: 'column',
                                        hidden: this.isHiddenReturnAdvanced,
                                        nullValue: Object.keys(this.travelModes()),
                                    },
                                    changes: {
                                        heading: `Changes${this.advancedReturnSuffix}`,
                                        type: 'ChangesInput',
                                        hidden: this.isHiddenReturnAdvanced,
                                        nullValue: {
                                            changes: null,
                                            time: null,
                                        },
                                    },
                                    operators: {
                                        heading: `Operators${this.advancedReturnSuffix}`,
                                        type: 'OperatorSelector',
                                        options: this.operators,
                                        hidden: this.isHiddenReturnAdvanced,
                                        nullValue: {
                                            include: [],
                                            exclude: [],
                                        },
                                    },
                                },
                            },
                            travelcardType: {
                                heading: 'Existing Travelcard Held',
                                // eslint-disable-next-line max-len
                                label: 'If you already hold a London Travelcard or a pass valid for travel across London\'s zones, select it here to benefit from boundary zone fares.',
                                type: 'RadioSelectors',
                                options: {
                                    0: 'None',
                                    1: 'Anytime Travelcard (inc. seasons)',
                                    2: 'Off-Peak Travelcard',
                                    3: 'Older Persons Freedom Pass',
                                    4: 'Disabled Persons Freedom Pass',
                                    5: 'Disabled Child Freedom Pass',
                                    6: 'TfL 60+ Oyster card',
                                },
                                style: 'radio',
                                nullValue: ['0'],
                                styleClass: 'column',
                            },
                            maxZoneCovered: {
                                heading: '',
                                type: 'DropdownList',
                                hidden: this.isHiddenMaxZoneCovered,
                                label: 'Zonal validity',
                                nullValue: '0',
                                options: [
                                    {
                                        value: 2,
                                        label: 'Zones 1-2',
                                    },
                                    {
                                        value: 3,
                                        label: 'Zones 1-3',
                                    },
                                    {
                                        value: 4,
                                        label: 'Zones 1-4',
                                    },
                                    {
                                        value: 5,
                                        label: 'Zones 1-5',
                                    },
                                    {
                                        value: 6,
                                        label: 'Zones 1-6',
                                    },
                                ],
                            },
                        },
                    },
                },
            };
        },
        selectedOptions() {
            return Object.keys(this.moreOptionsModalConfig.options)
                .filter((key) => {
                    const config = this.moreOptionsModalConfig.options[key];
                    const value = this.model.options[key];
                    if (key === 'railcard') {
                        return true;
                    }
                    if (this.isEqual(value, this.nullValue(config))) {
                        return false;
                    }
                    return value !== null && value !== false;
                });
        },
        requestBody() {
            const {
                adults,
                children,
                railcardsRequired,
            } = this.model.passengers;

            let { railcard } = this.model.passengers;
            const { advanced } = this.model.options;
            const advancedOut = this.advancedSearchParameters(advanced.outboundOptions);
            const advancedRtn = advanced.useSameOptions ? advancedOut : this.advancedSearchParameters(advanced.returnOptions);
            const {
                useSplitTickets,
                noSplitFaresLU,
            } = this.model.options;
            if (railcard === 'none') {
                railcard = null;
            }
            const travelcardType = parseInt(this.model.options.advanced.travelcardType ?? '0', 10);
            const maxZoneCovered = parseInt(this.model.options.advanced.maxZoneCovered ?? '0', 10);
            const luxonOutbound = this.getLuxonOutbound();
            const luxonReturn = this.getLuxonReturn();
            return {
                searchBy: this.model.searchBy,
                toStation: this.model.station.to.key,
                fromStation: this.model.station.from.key,
                outboundDate: luxonOutbound.toISO(),
                outboundDateDepartAfter: this.model.outbound.after,
                returnDate: luxonReturn !== null ? luxonReturn.toISO() : null,
                returnDateDepartAfter: this.model.return.after,
                adults: parseInt(adults, 10),
                children: parseInt(children, 10),
                railcard,
                railcardsRequired,
                useSplitTickets: useSplitTickets === true,
                noSplitFaresLU: noSplitFaresLU === true,
                firstClass: this.model.firstClass,
                flexible: this.model.flexible,
                intensiveSearch: advanced.intensive,
                advancedOutbound: advancedOut,
                advancedReturn: advancedRtn,
                travelcardType,
                maxZoneCovered,
            };
        },
        isRequestValid() {
            let isValid = true;
            isValid = this.model.station.to.key.length > 0 ? isValid : this.setInvalidReason('No \'To\' station selected');
            isValid = this.model.station.from.key.length > 0 ? isValid : this.setInvalidReason('No \'From\' station selected');
            if (!isValid) {
                return isValid;
            }
            isValid = this.model.station.from.key !== this.model.station.to.key
                ? isValid : this.setInvalidReason('From and to stations cannot be the same');
            isValid = this.model.outbound.date !== null ? isValid : this.setInvalidReason('Invalid departure date');

            isValid = this.$refs.paxPicker.isValid ? isValid
                : this.setInvalidReason('Problem with selected passengers/railcards');

            const earliestAllowedTime = DateTime.now()
                .minus({ minutes: 15 })
                .toJSDate();

            if (this.outboundDateTime.getTime() < earliestAllowedTime.getTime()) {
                isValid = this.setInvalidReason('Outbound date/time cannot be in the past');
            }

            if (this.model.return.date !== null) {
                if (this.returnDateTime.getTime() < earliestAllowedTime.getTime()) {
                    isValid = this.setInvalidReason('Return date/time cannot be in the past');
                }
                if (this.returnDateTime.getTime() < this.outboundDateTime.getTime()) {
                    isValid = this.setInvalidReason('Return date/time cannot be before departure date/time');
                }
            }

            return isValid;
        },
        outboundDateTime() {
            const outboundDateParts = this.model.outbound.date.split('-');
            const outboundTimeParts = this.model.outbound.time.split(':');
            return new Date(
outboundDateParts[0],
outboundDateParts[1] - 1,
outboundDateParts[2],
outboundTimeParts[0],
outboundTimeParts[1],
);
        },
        returnDateTime() {
            if (this.model.return.date !== null) {
                const returnDateParts = this.model.return.date.split('-');
                const returnTimeParts = this.model.return.time.split(':');
                return new Date(
returnDateParts[0],
returnDateParts[1] - 1,
returnDateParts[2],
returnTimeParts[0],
returnTimeParts[1],
);
            }
            return null;
        },
        isHiddenReturnAdvanced() {
            return !this.modifiedOptions ? true : this.modifiedOptions.advanced.useSameOptions;
        },
        isHiddenMaxZoneCovered() {
            return !this.modifiedOptions ? true
                : (this.modifiedOptions.advanced.travelcardType.length === 1
                    && (this.modifiedOptions.advanced.travelcardType[0] !== '1'));
        },
        advancedOutboundSuffix() {
            return !this.isHiddenReturnAdvanced ? ' - Outbound' : '';
        },
        advancedReturnSuffix() {
            return !this.isHiddenReturnAdvanced ? ' - Return' : '';
        },
        journeyPlannerInputClasses() {
            return {};
        },
        journeyPlannerSummaryClasses() {
            if (this.alwaysShowForm) {
                return {
                    'd-none': true,
                };
            }
            return {
                'd-flex': true,
                'd-md-none': true,
            };
        },
        buttonLabel() {
            if (this.alwaysShowForm) {
                return 'Find Tickets';
            }
            return 'Search Again';
        },
        buttonClasses() {
            return {
                'btn-primary': this.alwaysShowForm,
                'btn-blue-normal': !this.alwaysShowForm,
                'search-primary': this.alwaysShowForm,
            };
        },
    },
    watch: {
        // eslint-disable-next-line func-names
        'modifiedOptions.advanced.travelcardType': function (newVal, oldVal) {
            const singleNewVal = newVal[0];
            const singleOldVal = oldVal && oldVal.length > 0 ? oldVal[0] : null;
            if (singleNewVal === '1') {
                this.modifiedOptions.advanced.maxZoneCovered = '2';
            } else if (singleNewVal !== '0') {
                this.modifiedOptions.advanced.maxZoneCovered = '6';
            } else {
                this.modifiedOptions.advanced.maxZoneCovered = '0';
            }

            if ((singleNewVal === '1' || singleNewVal === '2') && (singleOldVal !== '1' && singleOldVal !== '2')) {
                // force scroll down
                Vue.nextTick(() => {
                    this.$refs.scrollableContent.querySelector('.dropdown-list').scrollIntoView();
                });
            }
        },
    },
    updated() {
        this.$refs.oneWayJourneyCheckbox.checked = this.model.return.date === null;
    },
    mounted() {
        let params = this.parameters;
        const handOverParams = document.querySelector('#searchParametersFromHandover')
            ? JSON.parse(decodeURIComponent($('#params-el').data('use-parameters').replace(/\+/g, ' '))) : null;

        const lsParams = window.sessionStorage.getItem('searchParameters');
        if (!handOverParams && lsParams && window.location.search.indexOf('no-saved-state') === -1) {
            params = JSON.parse(lsParams);
        } else if (handOverParams) {
            params = handOverParams;
        }

        // if there is state from the history API, use it.
        if (window.history.state) {
            params = window.history.state;
        }

        if (params) {
            if (params.fromStation) {
                this.model.station.from = this.getStationFromUIC(params.fromStation);
            }
            if (params.toStation) {
                this.model.station.to = this.getStationFromUIC(params.toStation);
            }
            this.model.searchBy = params.searchBy;

            if (params.outboundDate) {
                const parsedOutbound = DateTime.fromISO(params.outboundDate);
                this.model.outbound.date = parsedOutbound.toISODate();
                this.model.outbound.time = parsedOutbound.toISOTime({
                    suppressSeconds: true,
                    includeOffset: false,
                });
                if (this.outboundDateTime.getTime() < (new Date()).getTime()) {
                    const defaultValue = getDefaultNowBasedTime();
                    this.model.outbound.date = defaultValue.dateFormatted;
                    this.model.outbound.time = defaultValue.timeFormatted;
                }
            }
            this.model.outbound.after = params.outboundDateDepartAfter;

            if (params.returnDate) {
                const parsedReturn = DateTime.fromISO(params.returnDate);
                this.model.return.date = parsedReturn.toISODate();
                this.model.return.time = parsedReturn.toISOTime({
                    suppressSeconds: true,
                    includeOffset: false,
                });
                this.model.return.after = params.returnDateDepartAfter;
                if (this.returnDateTime !== null && this.returnDateTime.getTime() < (new Date()).getTime()) {
                    const defaultValue = getDefaultNowBasedReturnTime();
                    this.model.return.date = defaultValue.dateFormatted;
                    this.model.return.time = defaultValue.timeFormatted;
                }
            }

            this.model.passengers.adults = params.adults;
            if (params.railcardsRequired) {
                // Fix HTTP 500s from happening on searches with no railcards
                this.model.passengers.railcardsRequired = params.railcardsRequired;
            }
            this.model.passengers.children = params.children;
            this.model.passengers.railcard = params.railcard === null ? 'none' : params.railcard;
            if (params.travelcardType) {
                this.model.options.advanced.travelcardType = params.travelcardType;
                this.model.options.advanced.maxZoneCovered = params.maxZoneCovered;
            }
            this.model.options.useSplitTickets = params.useSplitTickets;
            if (params.intensiveSearch) {
                this.model.options.advanced.intensive = params.intensiveSearch;
            }
            this.model.options.noSplitFaresLU = params.noSplitFaresLU;
            if (params.advancedOutbound) {
                this.loadAdvancedParameters(
params.advancedOutbound,
                    this.model.options.advanced.outboundOptions,
);
            }

            if (params.advancedOutbound && params.advancedReturn) {
                this.model.options.advanced.useSameOptions = this.isEqual(
params.advancedOutbound,
                    params.advancedReturn,
);
                this.loadAdvancedParameters(params.advancedReturn, this.model.options.advanced.returnOptions);
            }
        }

        this.resetModifiedOptions();
        if (!this.model.station.from.key) {
            if (!(this.isMobile && this.isDirectus)) {
                this.$refs.fromStation.$refs.autosuggest.$el.querySelector('input')
                    .focus();
            }
        }

        if (window.location.search.indexOf('from-tcbc') !== -1) {
            this.model.options.useSplitTickets = false;
            $('#tcbc-ok').on('click', () => {
                $('#tcbc-migration').modal('hide');
            });
            $('#tcbc-migration').modal();
        }

        if (lsParams || window.history.state) {
            this.updateSearchSummaryLine();
        }

        this.$emit('loaded', this.requestBody);

        this.$on('remove-advanced-options', () => {
            this.remove('advanced');
            this.didClickSearch();
        });

        this.$on('copy-permalink', () => {
            navigator.clipboard.writeText(this.getPermalink());
            window.history.replaceState(null, null, this.getPermalink());
        });
    },
    methods: {
        isDevApi() {
            if (document.querySelector('meta[name=dev]') === null) {
                return false;
            }
            return document.querySelector('meta[name=dev]').getAttribute('content') === '1';
        },
        checkForConflictingOptions(ev) {
            if (ev.noSplitFaresLU && !ev.useSplitTickets) {
                this.$refs.moreOptionsGroup.update('useSplitTickets', true);
            }
        },
        lichfield() {
            this.model.passengers.adults = 0;
            this.model.passengers.children = 1;
            this.model.passengers.railcard = 'none';
            this.model.return.date = null;
            const defaultValue = getDefaultNowBasedTime();
            this.model.outbound.date = defaultValue.dateFormatted;
            this.model.outbound.time = defaultValue.timeFormatted;
            this.model.station.from = {
                key: '7011770',
                name: 'Lichfield City (LIC)',
            };
            this.model.station.to = {
                key: '7012910',
                name: 'Lichfield Trent Valley (LTV)',
            };
            this.model.options.useSplitTickets = true;
            this.model.options.noSplitFaresLU = false;
            this.didClickSearch();
        },
        updatePassengers(newOpts) {
            this.model.passengers.adults = newOpts.adults;
            this.model.passengers.children = newOpts.children;
            this.model.passengers.railcard = newOpts.railcard;
            this.model.passengers.railcardsRequired = newOpts.railcardsRequired;
            // If user presses apply on results page, do search but keep JP open
            if (window.location.pathname.startsWith('/results') && newOpts.railcardsRequired !== -1) {
                this.didClickSearch('', true);
            }
        },
        searchAgainIfNecessary() {
            if (!this.alwaysShowForm) {
                this.didClickSearch();
            }
        },
        setInvalidReason(reason) {
            this.invalidReason = reason;
            return false;
        },
        loadAdvancedParameters(params, options) {
            /* eslint-disable no-param-reassign */
            if (params.routes !== null && params.routes.via) {
                options.routes.via = params.routes.via;
            }
            if (params.routes !== null && params.routes.avoid) {
                options.routes.avoid = params.routes.avoid;
            }
            if (params.travelModes) {
                options.travelModes = Object.entries(params.travelModes)
                    .filter(([, value]) => value === 1)
                    .map(([key]) => key);
            }
            if (params.changes !== null && params.changes.changes !== null) {
                options.changes.changes = params.changes.changes;
            }
            if (params.changes !== null && params.changes.time) {
                options.changes.time = params.changes.time;
            }
            if (params.operators !== null && params.operators.include) {
                options.operators.include = params.operators.include;
            }
            if (params.operators !== null && params.operators.exclude) {
                options.operators.exclude = params.operators.exclude;
            }
            /* eslint-enable no-param-reassign */
        },
        clone(src) {
            const isArray = Array.isArray(src);
            const clone = isArray ? [] : {};
            Object.keys(src)
                .forEach((key) => {
                    if (src[key] instanceof Object) {
                        if (isArray) {
                            clone.push(this.clone(src[key]));
                        } else {
                            clone[key] = this.clone(src[key]);
                        }
                    } else if (isArray) {
                        clone.push(src[key]);
                    } else {
                        clone[key] = src[key];
                    }
                });
            return clone;
        },
        isValidDate(value) {
            return value.date !== null;
        },
        optionDisplay(key) {
            return this.moreOptionsModalConfig.options[key].label;
        },
        optionIcon(key) {
            return key === 'passengers' ? 'none' : 'remove';
        },
        optionList(max, unit, optionalPlural) {
            const plural = optionalPlural || 's';
            const list = [];
            for (let value = 0; value <= max; value++) {
                const labelValue = value === 0 ? 'No' : value;
                const labelUnit = value === 1 ? unit : `${unit}${plural}`;
                list.push({
                    value,
                    label: `${labelValue} ${labelUnit}`,
                });
            }
            return list;
        },
        remove(option) {
            this.model.options[option] = this.nullValue(this.moreOptionsModalConfig.options[option]);
            this.resetModifiedOptions();
        },
        clickOneWayJourney(e) {
          if (e.currentTarget.checked) {
              this.$refs.returnDatePicker.invalidateDate();
          } else {
              this.insertReturnDateAfterOutbound();
          }
        },
        showMoreOptions() {
            for (let x = 0; x < Object.keys(this.model.options).length; x++) {
                const key = Object.keys(this.model.options)[x];
                if (!this.model.options[key].value) {
                    this.model.options[key].value = true;
                    return;
                }
            }
        },
        updateSearchSummaryLine() {
            jQuery('.origin-station')
                .text(this.model.station.from.shortName !== undefined
                    ? this.model.station.from.shortName : this.model.station.from.name.slice(0, -6));
            jQuery('.destination-station')
                .text(this.model.station.to.shortName !== undefined
                    ? this.model.station.to.shortName : this.model.station.to.name.slice(0, -6));
            jQuery('.pax-and-railcards')
                .text(this.generatePaxSummaryStr());
        },
        updatePageTitle() {
            const shortMetaTitle = $('meta[name=brand-seo-meta-title-short]').attr('content');
            const from = $('.origin-station').text();
            const to = $('.destination-station').text();
            document.title = `${from} > ${to} - Search Results | ${shortMetaTitle}`;
        },
        didClickSearch(_, keepPlannerOpen = false) {
            if (!this.$refs.paxPicker.isLoadingRailcardValidity) {
                this.updateSearchSummaryLine();
                if (this.searchResultsTitle) {
                    this.updatePageTitle();
                }
                this.$emit('search', {
                    requestBody: this.requestBody,
                    keepPlannerOpen,
                });
            }
        },
        nullValue(config) {
            if (config.nullValue !== undefined) {
                return config.nullValue;
            }
            if (config.type === 'OptionsGroup') {
                const nullValues = {};
                Object.keys(config.options)
                    .forEach((key) => {
                        nullValues[key] = this.nullValue(config.options[key]);
                    });
                return nullValues;
            }
            return null;
        },
        isEqual(first, second) {
            if (first === second) {
                return true;
            }
            if (first !== null && second !== null) {
                if (Array.isArray(first) && Array.isArray(second)) {
                    if (first.length !== second.length) {
                        return false;
                    }
                }
            }
            if (first !== null && second !== null && typeof first === 'object') {
                let isEqual = true;
                if (Object.keys(first).length !== Object.keys(second).length) {
                    return false;
                }
                Object.keys(first)
                    .forEach((key) => {
                        if (!this.isEqual(first[key], second[key])) {
                            isEqual = false;
                        }
                    });
                return isEqual;
            }
            return false;
        },
        routeOptions() {
            return {
                stations: this.stations,
                method: {
                    via: {
                        P: 'Pass, stop or change at',
                        S: 'Stop or change at',
                        C: 'Change at',
                    },
                    avoid: {
                        P: 'Avoid completely',
                        S: 'Don\'t stop at',
                        C: 'Don\'t change at',
                    },
                },
            };
        },
        travelModes() {
            return {
                train: 'Train',
                bus: 'Bus',
                tube: 'Tube',
                metro: 'Metro',
                walk: 'Walk',
                transfer: 'Transfer',
                ship: 'Ship',
            };
        },
        resetModifiedOptions() {
            this.modifiedOptions = this.decode(this.model.options);
        },
        apply() {
            this.model.options = this.encode(this.modifiedOptions);
            $('#moreOptionsModal')
                .modal('hide');
            if (!this.alwaysShowForm) {
                this.didClickSearch();
            }
        },
        encode(options) {
            const clone = this.clone(options);
            const {
                outboundOptions,
                returnOptions,
            } = clone.advanced;

            outboundOptions.routes.via = this.encodeRoutes(outboundOptions.routes.via);
            outboundOptions.routes.avoid = this.encodeRoutes(outboundOptions.routes.avoid);
            returnOptions.routes.via = this.encodeRoutes(returnOptions.routes.via);
            returnOptions.routes.avoid = this.encodeRoutes(returnOptions.routes.avoid);
            return clone;
        },
        encodeRoutes(routes) {
            return routes.filter((route) => route.station.key.length > 0)
                .map((route) => {
                    const extra = parseInt(route.extra, 10);
                    if (route.method[0] === 'C' && extra > 0) {
                        return `${route.station.key}${route.method[0]}${extra}`;
                    }
                    return `${route.station.key}${route.method[0]}`;
                });
        },
        decode(options) {
            const clone = this.clone(options);
            const {
                outboundOptions,
                returnOptions,
            } = clone.advanced;
            outboundOptions.routes.via = this.decodeRoutes(outboundOptions.routes.via);
            outboundOptions.routes.avoid = this.decodeRoutes(outboundOptions.routes.avoid);
            returnOptions.routes.via = this.decodeRoutes(returnOptions.routes.via);
            returnOptions.routes.avoid = this.decodeRoutes(returnOptions.routes.avoid);
            return clone;
        },
        decodeRoutes(routes) {
            return routes.map((item) => {
                const regex = /(\d*)(P|S|C)?(\d*)/;
                const match = regex.exec(item);
                if (!match) {
                    return this.blankRoute();
                }
                const station = this.stationCache[match[1]] || this.getStationFromUIC(match[1]);
                const method = [match[2] !== undefined ? match[2] : 'P'];
                const extra = match[3];
                return {
                    station,
                    method,
                    extra,
                };
            })
                .concat(this.blankRoute());
        },
        advancedSearchParameters(model) {
            return {
                routes: {
                    via: this.nullIfEmpty(model.routes.via),
                    avoid: this.nullIfEmpty(model.routes.avoid),
                },
                travelModes: model.travelModes.length === Object.keys(this.travelModes()).length ? null : model.travelModes,
                changes: {
                    changes: model.changes.changes,
                    time: this.nullIfEmpty(model.changes.time),
                },
                operators: {
                    include: this.nullIfEmpty(model.operators.include),
                    exclude: this.nullIfEmpty(model.operators.exclude),
                },
            };
        },
        nullIfEmpty(model) {
            if (model === undefined || model === null) {
                return null;
            }
            if (typeof model === 'number') {
                return model === 0 ? null : model;
            }
            return model.length === 0 ? null : model;
        },
        getStationFromUIC(uic) {
            const station = this.stations.filter((item) => item.value === uic)
                .map((item) => ({
                    name: item.label,
                    key: item.value,
                }))[0];
            this.stationCache[uic] = station;
            return station;
        },
        blankRoute() {
            return {
                station: {
                    name: '',
                    key: '',
                },
                method: [
                    'P',
                ],
                extra: 0,
            };
        },
        stationChanged() {
            if (this.model.station.from.key === this.model.station.to.key) {
                $('#to-station .invalid-feedback')
                    .text('Stations must be different');
                $('#to-station input')
                    .addClass('is-invalid');
            } else {
                $('#to-station .invalid-feedback')
                    .text('');
                $('#to-station input')
                    .removeClass('is-invalid');
            }
        },
        insertReturnDateAfterOutbound() {
            const newDt = DateTime.fromISO(`${this.model.outbound.date}T${this.model.outbound.time}`)
                .plus({ hours: 2 });
            // eslint-disable-next-line prefer-destructuring
            this.model.return.date = newDt.toISODate();
            this.model.return.time = newDt.toLocaleString(DateTime.TIME_24_SIMPLE);
            this.model.return.dtObj = this.returnDateTime;
            this.model.return.after = this.model.outbound.after;
        },
        dateChanged(outboundChanged = false) {
            const earliestAllowedTime = DateTime.now().minus({ minutes: 15 }).toJSDate();

            if (this.outboundDateTime < earliestAllowedTime) {
                $('#outbound-date-warning')
                    .text('Outbound date is in the past');
            } else {
                $('#outbound-date-warning')
                    .text('');
            }

            this.$refs.oneWayJourneyCheckbox.checked = this.model.return.date === null;

            if (this.model.return.date !== null) {
                if (this.returnDateTime < earliestAllowedTime) {
                    $('#return-date .invalid-feedback')
                        .text('Return date is in the past');
                } else if (this.returnDateTime < this.outboundDateTime) {
                    if (outboundChanged) {
                        this.insertReturnDateAfterOutbound();
                    } else {
                        $('#return-date .invalid-feedback')
                            .text('Return date is before outbound');
                    }
                } else {
                    $('#return-date .invalid-feedback')
                        .text('');
                }
            } else {
                $('#return-date .invalid-feedback')
                    .text('');
            }
        },
        edit() {
            document.location = '/';
        },
        labelForListOptionSelection(key) {
            const config = this.moreOptionsModalConfig.options[key];
            const value = this.model.options[key];
            const {
                label,
                options,
            } = config;
            const matching = options.filter((option) => option.value === value);
            return matching.length > 0 ? matching[0].label : label;
        },
        labelForRailcard(code) {
            return this.railcards.filter((rc) => rc.value === code)[0].label;
        },
        generatePaxSummaryStr() {
            const { adults } = this.model.passengers;
            const { children } = this.model.passengers;
            const adultsPart = (adults > 0) ? `${adults} adult${adults !== 1 ? 's' : ''}` : '';
            const childrenPart = (children > 0) ? `${children} child${children !== 1 ? 'ren' : ''}` : '';
            const combinedPax = adultsPart + ((adults > 0 && children > 0) ? ', ' : '') + childrenPart;
            const railcard = this.model.passengers.railcard === 'none' ? ''
                : ` with ${this.labelForRailcard(this.model.passengers.railcard)}`;
            return combinedPax + railcard;
        },
        checkAdvancedSet() {
            const selected = this.selectedOptions;
            return selected.includes('advanced');
        },
        switchStations() {
            const fromStation = this.model.station.from;
            this.model.station.from = this.model.station.to;
            this.model.station.to = fromStation;
        },
        /** @return DateTime */
        getLuxonOutbound() {
            return DateTime.fromISO(
                `${this.model.outbound.date}T${this.model.outbound.time}`,
                { zone: 'Europe/London' },
            );
        },
        /** @return DateTime */
        getLuxonReturn() {
            let luxonReturn = null;
            if (this.model.return.date !== null) {
                luxonReturn = DateTime.fromISO(
                    `${this.model.return.date}T${this.model.return.time}`,
                    { zone: 'Europe/London' },
                );
            }
            return luxonReturn;
        },
        setLastSearchGuid(guid) {
            this.lastSearchGuid = guid;
        },
        getPermalink() {
            const builder = new URLSearchParams();
            const {
                adults,
                children,
                railcard,
            } = this.model.passengers;
            const luxonOutbound = this.getLuxonOutbound();
            const luxonReturn = this.getLuxonReturn();
            const { advanced } = this.model.options;
            const advancedOut = this.advancedSearchParameters(advanced.outboundOptions);
            const advancedRtn = advanced.useSameOptions ? advancedOut : this.advancedSearchParameters(advanced.returnOptions);

            builder.set('FromStation', this.model.station.from.key);
            builder.set('ToStation', this.model.station.to.key);
            if (parseInt(adults, 10) > 0) {
                builder.set('Adults', adults);
            }
            if (parseInt(children, 10) > 0) {
                builder.set('Children', children);
            }
            builder.set('OutboundDate', luxonOutbound.toFormat('dd/LL/yyyy'));
            builder.set('OutboundTime', luxonOutbound.toFormat('HH:mm'));
            if (luxonReturn !== null) {
                builder.set('InboundDate', luxonReturn.toFormat('dd/LL/yyyy'));
                builder.set('InboundTime', luxonReturn.toFormat('HH:mm'));
                if (this.model.outbound.after === this.model.return.after && !this.model.outbound.after) {
                    builder.set('LeaveAfter', '0');
                } else if (this.model.outbound.after !== this.model.return.after) {
                    builder.set('LeaveAfterOutbound', this.boolToInt(this.model.outbound.after));
                    builder.set('LeaveAfterInbound', this.boolToInt(this.model.return.after));
                }
            } else if (!this.model.outbound.after) {
                builder.set('LeaveAfter', '0');
            }
            if (!this.model.options.useSplitTickets) {
                builder.set('UseSplits', '0');
            }
            if (this.model.options.noSplitFaresLU) {
                builder.set('NoSplitFaresLU', '1');
            }
            if (advanced.intensive) {
                builder.set('Intensive', '1');
            }
            if (railcard !== 'none') {
                builder.set('Railcard', railcard);
            }
            if (advanced.travelcardType > 0) {
                builder.set('adv:tct', advanced.travelcardType);
                builder.set('adv:mzc', advanced.maxZoneCovered);
            }
            this.addAdvOptionsForSearchParams('ob', builder, advancedOut);
            if (advancedRtn) {
                this.addAdvOptionsForSearchParams('ib', builder, advancedRtn);
            }
            builder.set('searchBy', this.model.searchBy);
            builder.set('firstClass', this.model.firstClass);
            builder.set('flexible', this.model.flexible);
            if (document.querySelector('#let-me-choose-tab').classList.contains('active')) {
                builder.set('selectionMethod', 'Timeline');
            } else {
                builder.set('selectionMethod', 'Grid');
            }
            const url = new URL(`${window.location.origin}/results`);
            url.search = builder.toString();
            // Create the event
            const event = new CustomEvent('permalinkavailable', { detail: { url: url.toString(), guid: this.lastSearchGuid } });
            // Dispatch the event
            window.dispatchEvent(event);

            return url.toString();
        },
        /**
         * @param {string} prefix ib/ob
         * @param {URLSearchParams} builder The builder to use
         * @param {object} options The advanced options
         */
        addAdvOptionsForSearchParams(prefix, builder, options) {
            const key = `adv:${prefix}`;
            if (options.travelModes) {
                builder.set(`${key}:TravelModes`, options.travelModes.join(','));
            }
            if (options.changes.changes !== null) {
                builder.set(`${key}:MaxChanges`, options.changes.changes);
            }
            if (options.changes.time) {
                builder.set(`${key}:ExtraChangeTime`, options.changes.time);
            }
            if (options.operators.include) {
                builder.set(`${key}:IncludedTOCs`, options.operators.include.join(','));
            }
            if (options.operators.exclude) {
                builder.set(`${key}:ExcludedTOCs`, options.operators.exclude.join(','));
            }
            if (options.routes.via) {
                builder.set(`${key}:Via`, options.routes.via.join(','));
            }
            if (options.routes.avoid) {
                builder.set(`${key}:Avoid`, options.routes.avoid.join(','));
            }
        },
        boolToInt(input) {
            return input ? 1 : 0;
        },
    },
};
</script>
